import React, { memo } from 'react';

import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import CustomFieldInput from './components/CustomFieldInput';
import { CUSTOM_FIELDS_SECTION_TITLE, CUSTOM_FIELDS_TARGET } from './CustomFieldsSection.constants';
import {
  fieldItemStyles,
  fieldsGridStyles,
  sectionTitleStyles,
  sectionWrapperStyles,
} from './CustomFieldsSection.styles';
import { isCalculated, isManualReadOnly, isManualRequired } from './CustomFieldsSection.utils';
import useCustomFieldsValidation from './hooks/useCustomFieldsValidation';

const CustomFieldsSection = ({
  applicationId,
  target,
  targetEntityId,
  readonly = false,
  title = CUSTOM_FIELDS_SECTION_TITLE,
  dataTestId = 'custom-fields-section',
  controller = null,
}) => {
  const internalController = useCustomFieldsValidation({
    applicationId,
    target,
    targetEntityId,
    skip: !_.isNil(controller),
  });

  const activeController = controller ?? internalController;
  const {
    definitions,
    formValues,
    errors,
    pendingChangesByDefinition = {},
    setFieldValue,
    setFieldTouched,
  } = activeController;

  if (!definitions.length) {
    return null;
  }

  return (
    <Box data-testid={dataTestId} sx={sectionWrapperStyles}>
      <Typography sx={sectionTitleStyles} variant="medium16">
        {title}
      </Typography>
      <Box sx={fieldsGridStyles}>
        {definitions.map(definition => (
          <Box key={definition.id} sx={fieldItemStyles}>
            <CustomFieldInput
              dataTestId={`${dataTestId}-${definition.id}`}
              definition={definition}
              disabled={readonly || isManualReadOnly(definition) || isCalculated(definition)}
              errorKey={errors[definition.id]}
              pendingChangesActionType={pendingChangesByDefinition[definition.id] ?? null}
              required={isManualRequired(definition)}
              value={formValues[definition.id] ?? ''}
              onBlur={setFieldTouched}
              onChange={setFieldValue}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

CustomFieldsSection.propTypes = {
  applicationId: PropTypes.string.isRequired,
  target: PropTypes.oneOfType([
    PropTypes.oneOf(Object.values(CUSTOM_FIELDS_TARGET)),
    PropTypes.arrayOf(PropTypes.oneOf(Object.values(CUSTOM_FIELDS_TARGET))),
  ]).isRequired,
  targetEntityId: PropTypes.string.isRequired,
  readonly: PropTypes.bool,
  title: PropTypes.string,
  dataTestId: PropTypes.string,
  controller: PropTypes.shape({
    definitions: PropTypes.array.isRequired,
    formValues: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
    pendingChangesByDefinition: PropTypes.object,
  }),
};

export default memo(CustomFieldsSection);
